import { CheckIcon, MinusIcon, SparklesIcon } from "@heroicons/react/20/solid";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import PublicHeader from "../components/PublicHeader";
import { apiErrorMessage } from "../services/api";
import { defaultCurrencyCodes, fetchPublicPlans, planText } from "../services/signupService";
import { ctaHref } from "../services/subscriptionService";

/** The page's backdrop — the same two soft washes as the login screen. */
export const PUBLIC_BACKDROP = {
  background:
    "radial-gradient(1000px 420px at 12% -10%, rgb(104 231 178 / 0.18), transparent)," +
    "radial-gradient(900px 420px at 88% 110%, rgb(252 99 85 / 0.12), transparent)," +
    "var(--pl-bg)",
};

/**
 * Numbers stay Latin and left-to-right in both languages, with at most the
 * currency's own decimals (the server already rounded to them).
 */
const formatAmount = (price, decimals = 2) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(0, Math.min(2, Number(decimals ?? 2))),
  }).format(Number(price));

/** Card width: one column on phones, two on tablets, up to four side by side. */
const CARD_WIDTH = "w-full sm:w-[calc(50%-12px)] lg:w-[288px]";

/** An amount as the server sent it, with the currency symbol; Latin digits, left-to-right. */
export function Money({ amount, currency, className = "" }) {
  return (
    <span dir="ltr" className={`inline-flex items-baseline gap-1 tabular-nums ${className}`}>
      <span>{formatAmount(amount, currency?.decimals)}</span>
      {currency && <span className="text-[0.85em] font-semibold">{currency.symbol || currency.code}</span>}
    </span>
  );
}

/** The same amount as plain text, for interpolating into a sentence. */
export const moneyText = (amount, currency) =>
  `${formatAmount(amount, currency?.decimals)}${currency ? ` ${currency.symbol || currency.code}` : ""}`;

/**
 * The big per-month price of a cycle's quote, or "Free". Exported for the
 * register page's plan summary. The amount is the server's
 * `monthly_equivalent` — nothing is worked out here.
 */
export function PlanPrice({ plan, quote, size = "lg" }) {
  const { t } = useTranslation();
  const big = size === "lg";

  // A custom plan has no price: "Tailored pricing", and a contact button instead.
  if (plan.type === "custom") {
    return (
      <span className={`${big ? "text-section-title" : "text-lg"} font-bold text-ink`}>
        {t("pricing.customPrice")}
      </span>
    );
  }

  if (plan.is_free) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-2">
        <span className={`${big ? "text-display" : "text-xl"} font-bold text-ink`}>{t("pricing.free")}</span>
        {plan.trial_days ? (
          <span className="rounded-full bg-accent-wash px-2 py-0.5 text-xs font-bold text-accent-text">
            {t("pricing.trialDays", { count: plan.trial_days })}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
      <Money
        amount={quote ? quote.monthly_equivalent : plan.price}
        currency={plan.currency}
        className={`${big ? "text-display" : "text-xl"} font-bold text-ink`}
      />
      <span className="text-dense text-ink-2">{t("pricing.perMonth")}</span>
    </span>
  );
}

/** "Billed 2,149.20 every 12 months", the struck-through subtotal and "You save …". */
export function QuoteDetails({ plan, quote }) {
  const { t } = useTranslation();
  if (!quote || plan.is_free) return null;
  const discounted = Number(quote.savings_amount) > 0;

  return (
    <div className="mt-1.5 flex flex-col gap-0.5 text-sm text-ink-2">
      {quote.months > 1 && (
        <span>
          {t("pricing.billedEvery", { count: quote.months, total: moneyText(quote.total, plan.currency) })}
        </span>
      )}
      {discounted && (
        <span className="flex flex-wrap items-baseline gap-x-2">
          <Money amount={quote.subtotal} currency={plan.currency} className="line-through opacity-70" />
          <span className="font-semibold text-accent-text">
            {t("pricing.youSave", { amount: moneyText(quote.savings_amount, plan.currency) })}
          </span>
        </span>
      )}
    </div>
  );
}

/** One tab per public billing cycle, with a "Save N%" badge on discounted ones. */
export function CycleSwitcher({ cycles, value, onChange }) {
  const { t, i18n } = useTranslation();
  if (cycles.length < 2) return null;

  return (
    <div
      role="radiogroup"
      aria-label={t("pricing.cycles")}
      className="inline-flex max-w-full flex-wrap justify-center gap-1 rounded-card border border-line bg-surface p-1 shadow-e1"
    >
      {cycles.map((cycle) => {
        const selected = value === cycle.id;
        const discount = Number(cycle.discount_percent);
        return (
          <button
            key={cycle.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(cycle.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-fast ${
              selected ? "bg-accent text-accent-on" : "text-ink-2 hover:text-ink"
            }`}
          >
            {planText(cycle.name, i18n.language)}
            {discount > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none ${
                  selected ? "border border-current text-accent-on" : "bg-accent-wash text-accent-text"
                }`}
              >
                {t("pricing.saveBadge", { percent: formatAmount(cycle.discount_percent) })}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FeatureRow({ enabled, label, value }) {
  return (
    <li className={`flex items-start gap-2.5 text-dense ${enabled ? "text-ink" : "text-ink-2 opacity-70"}`}>
      {enabled ? (
        <CheckIcon
          className="mt-0.5 size-4 shrink-0 rounded-full bg-accent-wash p-0.5 text-accent-text"
          aria-hidden="true"
        />
      ) : (
        <MinusIcon className="mt-0.5 size-4 shrink-0 text-ink-2" aria-hidden="true" />
      )}
      <span className="flex-1">{label}</span>
      {value != null && (
        <span dir="ltr" className="font-semibold tabular-nums">
          {value}
        </span>
      )}
    </li>
  );
}

/**
 * The card's button, by type: paid → sign up on the cycle; free → sign up (no
 * cycle); custom → the "Contact us" link (never selectable). A trial the
 * signed-in business already used is shown disabled.
 */
export function PlanCta({ plan, quote, cycleId, recommended, onChoose, busy = false, current = false }) {
  const { t, i18n } = useTranslation();
  const style = `${recommended ? "btn-primary" : "btn-secondary"} block w-full text-center`;

  if (plan.type === "custom") {
    const href = ctaHref(plan.cta);
    const label = planText(plan.cta?.label, i18n.language) || t("pricing.contactUs");
    return href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className={style}>
        {label}
      </a>
    ) : (
      <button type="button" className={style} disabled>
        {label}
      </button>
    );
  }

  if (current) {
    return (
      <button type="button" className="btn-secondary block w-full text-center" disabled>
        {t("billing.currentPlan")}
      </button>
    );
  }

  if (plan.trial_available === false) {
    return (
      <>
        <button type="button" className="btn-secondary block w-full text-center" disabled>
          {t("pricing.choose")}
        </button>
        <p className="mt-2 text-center text-xs text-ink-2">{t("pricing.trialUsed")}</p>
      </>
    );
  }

  const offered = plan.type === "free" || Boolean(quote);
  const label = plan.trial_days ? t("pricing.startTrial") : t("pricing.choose");

  if (!offered) {
    return (
      <button type="button" className="btn-secondary block w-full text-center" disabled>
        {t("pricing.choose")}
      </button>
    );
  }

  // Signed in (billing page): choose here. Signed out: on to sign-up.
  if (onChoose) {
    return (
      <button type="button" className={style} onClick={() => onChoose(plan)} disabled={busy}>
        {label}
      </button>
    );
  }

  const params = new URLSearchParams({
    plan: plan.id,
    ...(plan.type === "paid" && cycleId ? { cycle: cycleId } : {}),
  });
  return (
    <Link to={`/register?${params}`} className={style}>
      {label}
    </Link>
  );
}

export function PlanCard({ plan, cycleId, onChoose, busy, current }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const recommended = plan.is_recommended;
  const quote = plan.prices?.[cycleId];

  return (
    <article
      className={`relative flex flex-col rounded-card border bg-surface p-6 transition duration-standard motion-reduce:transition-none ${CARD_WIDTH} ${
        recommended
          ? "order-first border-2 border-accent shadow-e2 motion-safe:hover:-translate-y-1 sm:order-none lg:-translate-y-3 lg:py-8 motion-safe:lg:hover:-translate-y-4"
          : "border-line shadow-e1 hover:shadow-e2 motion-safe:hover:-translate-y-1"
      }`}
      style={recommended ? { boxShadow: "0 18px 48px -18px var(--pl-accent), var(--pl-e2)" } : undefined}
      aria-label={planText(plan.name, lang)}
    >
      {recommended && (
        <span className="absolute inset-x-0 -top-3.5 mx-auto inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-on shadow-e1">
          <SparklesIcon className="size-3.5" aria-hidden="true" />
          {t("pricing.mostPopular")}
        </span>
      )}

      {/* 1. name + one-line description */}
      <h2 className="text-section-title font-bold text-ink">{planText(plan.name, lang)}</h2>
      <p className="mt-1 min-h-[40px] text-dense text-ink-2">{planText(plan.description, lang)}</p>

      {/* 2. price: the cycle's monthly equivalent, then what is billed */}
      <div className="mt-5 min-h-[88px]">
        {quote || plan.is_free || plan.type === "custom" ? (
          <>
            <PlanPrice plan={plan} quote={quote} />
            <QuoteDetails plan={plan} quote={quote} />
          </>
        ) : (
          <p className="text-dense text-ink-2">{t("pricing.notOffered")}</p>
        )}
      </div>

      {/* 3. divider */}
      <hr className="my-5 border-line" />

      {/* 4. limits, then feature flags */}
      <ul className="flex flex-col gap-2.5">
        {plan.limits.map((limit) => (
          <FeatureRow
            key={limit.key}
            enabled={limit.value !== 0}
            label={planText(limit.label, lang)}
            value={limit.value === null ? t("pricing.unlimited") : limit.value === 0 ? null : limit.value}
          />
        ))}
        {plan.features.map((feature) => (
          <FeatureRow key={feature.key} enabled={feature.enabled} label={planText(feature.label, lang)} />
        ))}
      </ul>

      {/* 5. pinned to the bottom so buttons line up across cards */}
      <div className="mt-auto pt-6">
        <PlanCta
          plan={plan}
          quote={quote}
          cycleId={cycleId}
          recommended={recommended}
          onChoose={onChoose}
          busy={busy}
          current={current}
        />
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className={`card flex flex-col p-6 ${CARD_WIDTH}`} aria-hidden="true">
      <div className="motion-safe:animate-pulse">
        <div className="h-6 w-1/2 rounded bg-line" />
        <div className="mt-3 h-4 w-3/4 rounded bg-line" />
        <div className="mt-6 h-10 w-2/3 rounded bg-line" />
        <div className="my-6 h-px bg-line" />
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="mb-3 h-4 rounded bg-line" style={{ width: `${85 - (i % 3) * 12}%` }} />
        ))}
        <div className="mt-6 h-10 rounded-control bg-line" />
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cycleId, setCycleId] = useState(null);

  const load = useCallback(() => {
    setError("");
    setData(null);
    fetchPublicPlans()
      .then((result) => {
        setData(result);
        // The server's pick (best discount, else monthly) unless one is chosen.
        setCycleId((current) =>
          current && result.billingCycles.some((c) => c.id === current)
            ? current
            : (result.defaultCycleId ?? result.billingCycles[0]?.id ?? null)
        );
      })
      .catch((err) => setError(apiErrorMessage(err, t("pricing.loadFailed"))));
  }, [t]);

  useEffect(load, [load]);

  const plans = data?.plans ?? null;
  const shown = plans ?? [];
  // No switcher: the server decides the currency. Only say so when it fell back to a default.
  const fallbackCodes = defaultCurrencyCodes(plans);

  return (
    <div className="min-h-screen" style={PUBLIC_BACKDROP}>
      <PublicHeader />

      <main className="mx-auto w-full max-w-7xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <h1 className="text-page-title font-bold text-ink sm:text-display">{t("pricing.title")}</h1>
          <p className="mt-3 text-body text-ink-2">{t("pricing.subtitle")}</p>
          <p className="mt-2 text-sm text-ink-2">{t("pricing.noPayment")}</p>
          {fallbackCodes.length > 0 && (
            <p className="mt-1 text-xs text-ink-2">
              {t("pricing.defaultCurrencyNote", { currencies: fallbackCodes.join(", ") })}
            </p>
          )}
        </div>

        {data && data.billingCycles.length > 1 && (
          <div className="mb-10 flex justify-center">
            <CycleSwitcher cycles={data.billingCycles} value={cycleId} onChange={setCycleId} />
          </div>
        )}

        {error ? (
          <div className="card mx-auto max-w-md p-6 text-center" role="alert">
            <p className="mb-4 text-danger-text">{error}</p>
            <button type="button" className="btn-primary" onClick={load}>
              {t("pricing.retry")}
            </button>
          </div>
        ) : plans === null ? (
          <div
            className="flex flex-wrap items-stretch justify-center gap-6"
            aria-busy="true"
            aria-label={t("pricing.loading")}
          >
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <p className="card mx-auto max-w-md p-6 text-center text-ink-2">{t("pricing.empty")}</p>
        ) : (
          <div className="flex flex-wrap items-stretch justify-center gap-6 pt-4">
            {shown.map((plan) => (
              <PlanCard key={plan.id} plan={plan} cycleId={cycleId} />
            ))}
          </div>
        )}

        <p className="mt-12 text-center text-sm text-ink-2">
          {t("pricing.haveAccount")}{" "}
          <Link to="/login" className="font-semibold text-accent-text hover:underline">
            {t("pricing.signIn")}
          </Link>
        </p>
      </main>
    </div>
  );
}
